import React, { useCallback, useEffect, useState } from 'react';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';
import SeasonFormModal from './SeasonFormModal';
import Avatar from '../ui/Avatar';

interface Standing {
  id: string;
  matchesPlayed: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  user: { id: string; name: string; avatar?: string | null };
}

interface Season {
  id: string;
  name: string;
  isActive: boolean;
  startDate: string;
  endDate?: string | null;
  pointsForWin?: number;
  pointsForDraw?: number;
  pointsForLoss?: number;
  _count?: { seasonMatches: number };
}

interface SeasonStandingsProps {
  groupId: string;
  isAdmin?: boolean;
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const SeasonStandings: React.FC<SeasonStandingsProps> = ({ groupId, isAdmin = false }) => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [activeSeason, setActiveSeason] = useState<Season | null>(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingSeason, setEditingSeason] = useState<Season | null>(null);
  const [deletingSeason, setDeletingSeason] = useState<Season | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const fetchStandings = useCallback(async (seasonId: string) => {
    try {
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/seasons/${seasonId}`), {
        headers: authHeaders(),
      });
      const json = res.ok ? await res.json() : null;
      if (json?.standings) setStandings(json.standings);
    } catch {
      // ignore
    }
  }, [groupId]);

  const fetchSeasons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/seasons`), {
        headers: authHeaders(),
      });
      const json = res.ok ? await res.json() : null;
      const list: Season[] = json?.seasons || [];
      setSeasons(list);
      const active = list.find((s) => s.isActive) || list[0] || null;
      setActiveSeason(active);
      setSelectedSeasonId(active?.id || null);
      if (active) await fetchStandings(active.id);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [groupId, fetchStandings]);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  const selectSeason = async (seasonId: string) => {
    setSelectedSeasonId(seasonId);
    const found = seasons.find((s) => s.id === seasonId) || null;
    setActiveSeason(found);
    setStandings([]);
    if (found) await fetchStandings(found.id);
  };

  const handleDelete = async () => {
    if (!deletingSeason) return;
    setDeleteSubmitting(true);
    try {
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/seasons/${deletingSeason.id}`), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        setDeletingSeason(null);
        await fetchSeasons();
      }
    } catch {
      // ignore
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: theme.colors.textSecondary }}>
        Cargando temporadas...
      </div>
    );
  }

  if (seasons.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 56, opacity: 0.5 }}>🏆</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>No hay temporada activa</div>
        <div style={{ fontSize: 13, color: theme.colors.textSecondary, maxWidth: 320, lineHeight: 1.5 }}>
          {isAdmin
            ? 'Creá una temporada para llevar la tabla de posiciones del grupo.'
            : 'El administrador del grupo puede crear una temporada.'}
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 8,
              padding: '10px 20px',
              borderRadius: 10,
              border: 'none',
              background: theme.colors.primary,
              color: theme.colors.background,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ＋ Crear temporada
          </button>
        )}
        {showCreate && (
          <SeasonFormModal
            groupId={groupId}
            mode="create"
            onClose={() => setShowCreate(false)}
            onSaved={() => fetchSeasons()}
          />
        )}
      </div>
    );
  }

  const numCol: React.CSSProperties = { width: 38, textAlign: 'center', fontSize: 13, color: theme.colors.text };
  const headerCell: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
          <span style={{ fontSize: 22 }}>🏆</span>
          {seasons.length === 1 ? (
            <span style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>
              {activeSeason?.name}
            </span>
          ) : (
            <select
              value={selectedSeasonId || ''}
              onChange={(e) => selectSeason(e.target.value)}
              style={{
                background: theme.colors.background,
                color: theme.colors.text,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: 8,
                padding: '6px 10px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {seasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.isActive ? ' · activa' : ''}
                </option>
              ))}
            </select>
          )}
          {activeSeason?._count?.seasonMatches != null && (
            <span style={{ fontSize: 12, color: theme.colors.textSecondary }}>
              · {activeSeason._count.seasonMatches} partidos
            </span>
          )}
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {activeSeason && (
              <>
                <button
                  type="button"
                  onClick={() => setEditingSeason(activeSeason)}
                  title="Editar temporada"
                  style={{
                    padding: '6px 10px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
                    background: 'transparent', color: theme.colors.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  ✏️ Editar
                </button>
                <button
                  type="button"
                  onClick={() => setDeletingSeason(activeSeason)}
                  title="Borrar temporada"
                  style={{
                    padding: '6px 10px', borderRadius: 8, border: `1px solid ${theme.colors.danger}`,
                    background: 'transparent', color: theme.colors.danger, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  🗑️
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              style={{
                padding: '6px 12px', borderRadius: 8, border: `1px solid ${theme.colors.primary}`,
                background: 'rgba(0,230,118,0.12)', color: theme.colors.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              ＋ Nueva
            </button>
          </div>
        )}
      </div>

      {standings.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
          Todavía no hay resultados. Los puntos se calculan cuando terminan los partidos.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '32px 1fr 38px 38px 38px 38px 38px 44px',
              alignItems: 'center',
              padding: '8px 0',
              borderBottom: `1px solid ${theme.colors.border}`,
              minWidth: 460,
            }}
          >
            <div style={{ ...headerCell, textAlign: 'center' }}>#</div>
            <div style={{ ...headerCell, textAlign: 'left', paddingLeft: 8 }}>Jugador</div>
            <div style={headerCell}>PJ</div>
            <div style={headerCell}>G</div>
            <div style={headerCell}>E</div>
            <div style={headerCell}>P</div>
            <div style={headerCell}>GF</div>
            <div style={headerCell}>Pts</div>
          </div>
          {standings.map((s, i) => (
            <div
              key={s.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '32px 1fr 38px 38px 38px 38px 38px 44px',
                alignItems: 'center',
                padding: '10px 0',
                background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                minWidth: 460,
              }}
            >
              <div style={{ textAlign: 'center', fontSize: i < 3 ? 16 : 13, color: theme.colors.text, fontWeight: 700 }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8, minWidth: 0 }}>
                <Avatar src={s.user.avatar} name={s.user.name} size={26} bg={theme.colors.border} />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: theme.colors.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.user.name}
                </span>
              </div>
              <div style={numCol}>{s.matchesPlayed}</div>
              <div style={{ ...numCol, color: '#4CAF50', fontWeight: 600 }}>{s.wins}</div>
              <div style={numCol}>{s.draws}</div>
              <div style={{ ...numCol, color: '#EF5350' }}>{s.losses}</div>
              <div style={numCol}>{s.goalsFor}</div>
              <div style={{ ...numCol, color: '#F59E0B', fontWeight: 800, fontSize: 14 }}>{s.points}</div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <SeasonFormModal
          groupId={groupId}
          mode="create"
          onClose={() => setShowCreate(false)}
          onSaved={() => fetchSeasons()}
        />
      )}

      {editingSeason && (
        <SeasonFormModal
          groupId={groupId}
          mode="edit"
          season={editingSeason}
          onClose={() => setEditingSeason(null)}
          onSaved={() => fetchSeasons()}
        />
      )}

      {deletingSeason && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1003, padding: 16,
          }}
          onClick={() => !deleteSubmitting && setDeletingSeason(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: theme.colors.surface, borderRadius: 16, width: 380, maxWidth: '100%', padding: 24 }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: theme.colors.text }}>
              Borrar temporada
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: theme.colors.textSecondary, lineHeight: 1.5 }}>
              ¿Seguro que querés borrar <strong>{deletingSeason.name}</strong>? Se eliminan sus posiciones. Los partidos del grupo no se borran.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDeletingSeason(null)}
                disabled={deleteSubmitting}
                style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${theme.colors.border}`, background: 'transparent', color: theme.colors.text, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteSubmitting}
                style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: theme.colors.danger, color: '#fff', fontSize: 14, fontWeight: 700, cursor: deleteSubmitting ? 'not-allowed' : 'pointer', opacity: deleteSubmitting ? 0.6 : 1 }}
              >
                {deleteSubmitting ? 'Borrando...' : 'Borrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeasonStandings;
